export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function apiFetch<T = unknown>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const res = await fetch(path, { credentials: 'include', ...init });
  if (!res.ok) {
    let body: { error?: string } = { error: res.statusText };
    try {
      body = await res.json();
    } catch {
      // non-JSON body; keep default
    }
    const message = body?.error ?? `HTTP ${res.status}`;
    console.error(`[api] ${method} ${path} → ${res.status}`, body);
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<T>;
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

// ── App config (public; matches server env) ───────────────────────────────────
export const appConfig = {
  get: () => apiFetch<{ igluAdminOrigin: string }>('/api/config'),
};

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  login: (email: string, password: string) =>
    apiFetch('/api/auth/login', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, password }) }),
  verify: (email: string, code: string) =>
    apiFetch('/api/auth/verify', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, otp: code }) }),
  logout: () => apiFetch('/api/auth/logout', { method: 'POST' }),
  me: () => apiFetch<{ userId: string; email: string; enableEdit?: boolean }>('/api/auth/me'),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboard = {
  get: () => apiFetch<DashboardData>('/api/catalog/dashboard'),
};

export interface DashboardData {
  upcomingCount: number;
  atRiskCount: number;
  mirrorCount: number;
  batchesThisMonth: number;
  upcomingItems: CatalogItem[];
  atRiskItems: CatalogItem[];
  recentBatches: Array<{
    id: string;
    fileName: string;
    source: string;
    status: string;
    totalRecords: number;
    readyCount: number;
    createdAt: string;
  }>;
  lastSync: string | null;
}

// ── Redemption codes ─────────────────────────────────────────────────────────
export interface RedemptionCode {
  _id: string;
  code: string;
  status?: string;
  redeemedAt?: string;
  createdAt?: string;
}

export interface RedemptionGroup {
  name: string;
  productName: string;
  productType?: 'Item' | 'Group';
  seriesId?: string;
  createdAt?: string;
  codeCount: number;
  redeemedCount: number;
  codes: RedemptionCode[];
}

export interface RedemptionListResponse {
  groups: RedemptionGroup[];
  redemptionNames: string[];
  total: number;
  totalCodes: number;
  page: number;
  pages: number;
}

export interface CreatedRedemptionCsv {
  csv: string;
  filename: string;
}

export const redemptions = {
  list: (page = 1, q = '') => {
    const params = new URLSearchParams({ page: String(page), limit: '25' });
    if (q) params.set('q', q);
    return apiFetch<RedemptionListResponse>(`/api/redemptions?${params}`);
  },
  create: async (input: {
    numbersToCreate: number;
    redemptionName: string;
    item?: string;
    group?: string;
  }): Promise<CreatedRedemptionCsv> => {
    const res = await fetch('/api/redemptions', {
      method: 'POST',
      credentials: 'include',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json() as { error?: string };
        if (body.error) message = body.error;
      } catch {
        // Keep HTTP status text.
      }
      throw new ApiError(res.status, message);
    }
    const csv = await res.text();
    const disposition = res.headers.get('Content-Disposition');
    const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? 'redemption-codes.csv';
    return { csv, filename };
  },
};

// ── Stripe coupons ────────────────────────────────────────────────────────────
export interface CouponCode {
  id: string;
  code: string;
  active: boolean;
  timesRedeemed: number;
  maxRedemptions: number | null;
  expiresAt: number | null;
  customerId: string | null;
  firstTimeTransaction: boolean;
  minimumAmount: number | null;
  minimumAmountCurrency: string | null;
}

export interface StripeCoupon {
  id: string;
  name: string | null;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  redeemBy: number | null;
  timesRedeemed: number;
  valid: boolean;
  appliesToAll: boolean;
  productCount: number;
  sampleNames: string[];
  publisherIds: string[];
  includeBundles: boolean;
  includeUnpublished: boolean;
  includeArchived: boolean;
  minPrice: number | null;
  ours: boolean;
  codes: CouponCode[];
}

export interface CouponDraftPayload {
  name: string;
  code?: string;
  discount: { type: 'percent'; percentOff: number } | { type: 'amount'; amountOff: number; currency: string };
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths?: number | null;
  maxRedemptions?: number | null;
  redeemBy?: string | null;
  appliesToAll: boolean;
  publisherIds: string[];
  itemIds: string[];
  groupIds: string[];
  includeBundles: boolean;
  includeUnpublished: boolean;
  includeArchived: boolean;
  minPrice?: number | null;
  customer?: string | null;
  promoMaxRedemptions?: number | null;
  expiresAt?: string | null;
  firstTimeTransaction?: boolean;
  minimumAmount?: number | null;
  minimumAmountCurrency?: string | null;
}

export interface CouponPreview {
  appliesToAll: boolean;
  productIds: string[];
  itemCount: number;
  bundleCount: number;
  sampleNames: string[];
  missingExplicit: string[];
  productLimit: number;
}

export const coupons = {
  list: () => apiFetch<{ mode: 'test' | 'live'; truncated: boolean; warning: string | null; coupons: StripeCoupon[] }>('/api/coupons'),
  preview: (body: Pick<CouponDraftPayload, 'appliesToAll' | 'publisherIds' | 'itemIds' | 'groupIds' | 'includeBundles' | 'includeUnpublished' | 'includeArchived' | 'minPrice'>) =>
    apiFetch<CouponPreview>('/api/coupons/preview', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }),
  create: (body: CouponDraftPayload) =>
    apiFetch<StripeCoupon>('/api/coupons', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }),
  rename: (id: string, name: string) =>
    apiFetch<StripeCoupon>(`/api/coupons/${id}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ name }) }),
  addCode: (id: string, body: Pick<CouponDraftPayload, 'code' | 'customer' | 'promoMaxRedemptions' | 'expiresAt' | 'firstTimeTransaction' | 'minimumAmount' | 'minimumAmountCurrency'>) =>
    apiFetch<StripeCoupon>(`/api/coupons/${id}/codes`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }),
  reissue: (id: string, body: CouponDraftPayload) =>
    apiFetch<StripeCoupon>(`/api/coupons/${id}/reissue`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) }),
  setCodeActive: (id: string, active: boolean) =>
    apiFetch<{ ok: boolean }>(`/api/coupons/codes/${id}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ active }) }),
  customers: (q: string) => apiFetch<{ customers: Array<{ id: string; label: string }> }>(`/api/coupons/customers?q=${encodeURIComponent(q)}`),
};

// ── Batches ───────────────────────────────────────────────────────────────────
export const batches = {
  list: (page = 1, status?: string, source?: string) => {
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    return apiFetch<BatchListResponse>(`/api/batches?${params}`);
  },
  get: (id: string) => apiFetch<Batch>(`/api/batches/${id}`),
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch<{ batchId: string; alreadyApplied?: boolean }>('/api/batches/upload', {
      method: 'POST',
      body: form,
    });
  },
  records: (id: string, page = 1, status?: string, diffAction?: string) => {
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set('status', status);
    if (diffAction) params.set('diffAction', diffAction);
    return apiFetch<RecordListResponse>(`/api/batches/${id}/records?${params}`);
  },
  apply: (batchId: string) =>
    apiFetch<{ ok: boolean }>(`/api/batches/${batchId}/apply`, { method: 'POST' }),
  retry: (batchId: string) =>
    apiFetch<{ ok: boolean }>(`/api/batches/${batchId}/apply/retry`, { method: 'POST' }),
  audit: (batchId: string) =>
    apiFetch<BatchAuditEntry[]>(`/api/batches/${batchId}/audit`),
};

export interface BatchAuditEntry {
  id: string;
  batchId: string;
  action: string;
  detail?: unknown;
  createdAt: string;
}

// ── Records ───────────────────────────────────────────────────────────────────
export const records = {
  get: (id: string) => apiFetch<ImportRecord>(`/api/records/${id}`),
  approve: (id: string, overrides?: { format?: string; type?: string }) =>
    apiFetch(`/api/records/${id}/approve`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(overrides ?? {}),
    }),
  reject: (id: string) =>
    apiFetch(`/api/records/${id}/reject`, { method: 'POST' }),
  bulkApprove: (ids: string[], batchId: string) =>
    apiFetch('/api/records/bulk-approve', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ ids, batchId }),
    }),
  bulkReject: (ids: string[], batchId: string) =>
    apiFetch('/api/records/bulk-reject', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ ids, batchId }),
    }),
};

// ── CSV ───────────────────────────────────────────────────────────────────────
export const csv = {
  uploadCsv: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiFetch<CsvUploadResponse>('/api/batches/upload-csv', { method: 'POST', body: form });
  },
  preview: (batchId: string) =>
    apiFetch<CsvPreviewResponse>(`/api/batches/${batchId}/csv-preview`),
  submitMapping: (batchId: string, mapping: CsvMappingConfig, saveAs?: string) =>
    apiFetch<{ ok: boolean; batchId: string }>(`/api/batches/${batchId}/csv-map`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ mapping, saveAs }),
    }),
  savedMappings: () =>
    apiFetch<{ mappings: SavedCsvMapping[] }>('/api/batches/csv-mappings/list'),
};

export interface CsvUploadResponse {
  batchId: string;
  headers: string[];
  sampleRows: Record<string, string>[];
  alreadyApplied?: boolean;
  status?: string;
}

export interface CsvPreviewResponse {
  headers: string[];
  sampleRows: Record<string, string>[];
}

export interface CsvColumnMapping {
  csvHeader: string;
  canonicalField: string;
  role?: string;
  delimiter?: string;
}

export interface CsvBatchDefaults {
  seriesName?: string;
  groupName?: string;
  publisherName?: string;
  genre?: string;
  status?: string;
  format?: string;
  type?: string;
  language?: string;
}

export interface CsvMappingConfig {
  columns: CsvColumnMapping[];
  defaults: CsvBatchDefaults;
}

export interface SavedCsvMapping {
  id: string;
  name: string;
  mappingJson: CsvMappingConfig;
  createdAt: string;
}

// ── Catalog ───────────────────────────────────────────────────────────────────
export const catalogAdmin = {
  // Series
  seriesDetail: (id: string) => apiFetch<any>(`/api/catalog/series/${id}`),
  updateSeries: (id: string, data: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>(`/api/catalog/series/${id}`, {
      method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(data),
    }),
  // Groups
  groupDetail: (id: string) => apiFetch<any>(`/api/catalog/groups/${id}`),
  updateGroup: (id: string, data: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>(`/api/catalog/groups/${id}`, {
      method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(data),
    }),
  bulkPatchItems: (ids: string[], patch: { status?: string; isArchived?: boolean }) =>
    apiFetch<{ succeeded: string[]; failed: Array<{ id: string; error: string }>; total: number }>(
      '/api/catalog/items/bulk-patch',
      { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ids, patch }) },
    ),

  // Contributors
  contributorDetail: (id: string) => apiFetch<any>(`/api/catalog/contributors/${id}`),
  contributorAppearances: (id: string) =>
    apiFetch<{ items: Array<{ _id: string; name: string; status?: string; releaseAt?: string; image?: string; seriesId?: string }>; series: Array<{ _id: string; name: string; status?: string; image?: string }> }>(`/api/catalog/contributors/${id}/appearances`),
  updateContributor: (id: string, data: Record<string, unknown>) =>
    apiFetch<{ ok: boolean }>(`/api/catalog/contributors/${id}`, {
      method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(data),
    }),
  contributorDuplicates: () => apiFetch<ContributorDuplicatesData>('/api/catalog/contributors/duplicates'),
  // Publishers
  publisherDetail: (id: string) => apiFetch<Publisher>(`/api/catalog/publishers/${id}`),
  // Genres
  genreDetail: (id: string) => apiFetch<Genre>(`/api/catalog/genres/${id}`),
};

/** Stored filters for a named catalog view (matches CatalogParams + sort). */
export type CatalogAdvFilterOp = 'eq' | 'neq' | 'contains' | 'empty' | 'not_empty';

export interface CatalogAdvFilter {
  field: string;
  op: CatalogAdvFilterOp;
  value: string;
}

export interface CatalogSavedViewFilters {
  search?: string;
  status?: string;
  upcoming?: boolean;
  upcomingDays?: number;
  atRisk?: boolean;
  auditFlags?: string[];
  /** Multi-row field filters (AND). */
  advancedFilters?: CatalogAdvFilter[];
  /** @deprecated Old saved views only — use `advancedFilters`. */
  missingField?: string;
  /** Omitted/true = hide archived (default). `false` = include archived rows. */
  hideArchived?: boolean;
  publisher?: string;
  series?: string;
  group?: string;
  sortBy?: 'name' | 'status' | 'releaseAt' | 'preorderReleaseAt' | 'flags';
  sortDir?: 'asc' | 'desc';
}

export interface CatalogSavedView {
  id: string;
  userId: string;
  name: string;
  filtersJson: CatalogSavedViewFilters;
  createdAt: string;
  updatedAt: string;
}

function buildCatalogItemsQueryParams(params: CatalogParams = {}): URLSearchParams {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status) q.set('status', params.status);
  if (params.upcoming) q.set('upcoming', 'true');
  if (params.upcoming && params.upcomingDays != null) {
    q.set('upcomingDays', String(params.upcomingDays));
  }
  if (params.atRisk) q.set('atRisk', 'true');
  if (params.auditFlags?.length) q.set('flags', params.auditFlags.join(','));
  if (params.advancedFilters?.length) {
    q.set('adv', encodeURIComponent(JSON.stringify(params.advancedFilters)));
  }
  if (params.hideArchived === false) q.set('hideArchived', 'false');
  if (params.publisher) q.set('publisher', params.publisher);
  if (params.series) q.set('series', params.series);
  if (params.group) q.set('group', params.group);
  if (params.page) q.set('page', String(params.page));
  if (params.limit != null) q.set('limit', String(params.limit));
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortDir) q.set('sortDir', params.sortDir);
  return q;
}

export const catalog = {
  items: (params: CatalogParams = {}) => {
    const q = buildCatalogItemsQueryParams(params);
    return apiFetch<CatalogListResponse>(`/api/catalog/items?${q}`);
  },
  /** Download CSV for current list filters (server caps rows). */
  exportItemsCsv: async (params: CatalogParams = {}): Promise<void> => {
    const q = buildCatalogItemsQueryParams(params);
    q.delete('page');
    q.delete('limit');
    const res = await fetch(`/api/catalog/items/export?${q}`, { credentials: 'include' });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) message = body.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, message);
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition');
    let filename = 'catalog-export.csv';
    const m = cd?.match(/filename="([^"]+)"/);
    if (m?.[1]) filename = m[1];
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  item: (id: string) => apiFetch<CatalogItem>(`/api/catalog/items/${id}`),
  recheckAudio: (id: string) =>
    apiFetch<AudioRecheckResult>(`/api/catalog/items/${id}/recheck-audio`, { method: 'POST' }),
  series: () => apiFetch<{ series: Series[] }>('/api/catalog/series'),
  publishers: () => apiFetch<{ publishers: Publisher[] }>('/api/catalog/publishers'),
  imprints: () => apiFetch<{ imprints: Imprint[] }>('/api/catalog/imprints'),
  groups: (seriesId?: string, search?: string) => {
    const params = new URLSearchParams();
    if (seriesId) params.set('seriesId', seriesId);
    if (search) params.set('search', search);
    const q = params.toString();
    return apiFetch<{ groups: Group[] }>(`/api/catalog/groups${q ? `?${q}` : ''}`);
  },
  contributors: (search?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (limit != null) params.set('limit', String(limit));
    const q = params.toString();
    return apiFetch<{ contributors: Contributor[] }>(
      `/api/catalog/contributors${q ? `?${q}` : ''}`,
    );
  },
  genres: () => apiFetch<{ genres: Genre[] }>('/api/catalog/genres'),
  updateItem: (id: string, data: Record<string, unknown>) =>
    apiFetch<CatalogItem>(`/api/catalog/items/${id}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify(data),
    }),
  sync: () =>
    apiFetch<{ ok: boolean; counts: Record<string, number>; error?: string }>('/api/catalog/sync', {
      method: 'POST',
    }),
  syncStatus: () =>
    apiFetch<{ logs: any[]; mirrorCounts: { items: number; series: number } }>('/api/catalog/sync/status'),

  views: {
    list: () => apiFetch<{ views: CatalogSavedView[] }>('/api/catalog/views'),
    create: (name: string, filtersJson: CatalogSavedViewFilters) =>
      apiFetch<CatalogSavedView>('/api/catalog/views', {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ name, filtersJson }),
      }),
    update: (id: string, body: { name?: string; filtersJson?: CatalogSavedViewFilters }) =>
      apiFetch<CatalogSavedView>(`/api/catalog/views/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify(body),
      }),
    delete: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/catalog/views/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },
};

// ── Types ─────────────────────────────────────────────────────────────────────
export interface Batch {
  id: string;
  fileName: string;
  status: string;
  totalRecords: number;
  readyCount: number;
  reviewCount: number;
  blockedCount: number;
  noOpCount: number;
  uploadedBy: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BatchListResponse {
  batches: Batch[];
  total: number;
  page: number;
  pages: number;
}

export interface ImportRecord {
  id: string;
  batchId: string;
  isbn13?: string;
  bookId?: string;
  adminId?: string;
  entityType: string;
  canonicalJson: Record<string, unknown>;
  diffJson?: { action: string; fields: FieldDiff[]; hasHighRisk: boolean };
  diffAction?: string;
  status: string;
  validationErrors?: Array<{ field: string; message: string; severity: string }>;
  applyError?: string;
  createdAt: string;
}

export interface FieldDiff {
  field: string;
  incoming: unknown;
  existing: unknown;
  risk: string;
}

export interface RecordListResponse {
  records: ImportRecord[];
  total: number;
  page: number;
  pages: number;
}

export interface CatalogItem {
  _id: string;
  name: string;
  slug?: string;
  isbn13?: string;
  status?: string;
  releaseAt?: string;
  description?: string;
  publishers?: string[];
  genres?: string[];
  cast?: Array<{ teamMember: string; type: string }>;
  seriesId?: string;
  groupId?: string;
  coverImageUrl?: string;
  image?: string;
  price?: number;
  language?: string;
  atRiskFlags?: string[];
  importHistory?: Array<{ id: string; batchId: string; diffAction: string; createdAt: string }>;
  [key: string]: unknown;
}

export interface AudioRecheckResult {
  has96kAudio: boolean;
  has192kAudio: boolean;
  resourceCount: number;
  missingLowRes: string[];
  missingHighRes: string[];
}

export interface CatalogListResponse {
  items: CatalogItem[];
  total: number;
  page?: number;
  pages?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CatalogParams {
  search?: string;
  status?: string;
  upcoming?: boolean;
  /** When `upcoming` is true, narrow release window (backend default 30). */
  upcomingDays?: number;
  atRisk?: boolean;
  auditFlags?: string[];
  advancedFilters?: CatalogAdvFilter[];
  /** Omit or true = exclude `isArchived` (default). `false` = include archived. */
  hideArchived?: boolean;
  publisher?: string;
  series?: string;
  group?: string;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'status' | 'releaseAt' | 'preorderReleaseAt' | 'flags';
  sortDir?: 'asc' | 'desc';
}

export interface Series {
  _id: string;
  name: string;
  slug?: string;
  status?: string;
  image?: string;
  releaseAt?: string;
  isArchived?: boolean;
  publisherIds?: string[];
  genreIds?: string[];
  groupCount?: number;
  itemCount?: number;
}

export interface Publisher {
  _id: string;
  key: string;
  value: string;
}

/** Same shape as publishers — Iglu enum docs */
export type Imprint = Publisher;

export interface Group {
  _id: string;
  name: string;
  seriesId: string;
  seriesName?: string | null;
  status?: string;
  slug?: string;
  releaseAt?: string;
}

export interface Contributor {
  _id: string;
  name: string;
}

export interface ContributorDuplicateGroup {
  confidence: 'exact' | 'maybe';
  members: Array<{ _id: string; name: string; image?: string | null }>;
}

export interface ContributorDuplicatesData {
  groups: ContributorDuplicateGroup[];
  totalGroups: number;
  exactCount: number;
  maybeCount: number;
}

export interface Genre {
  _id: string;
  name: string;
  slug?: string;
}
